import { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Briefcase, CheckCircle, ArrowRight, ArrowLeft, Loader2, 
  Rocket, Building2, FileText, Shield, User, Wallet, Bell,
  Gift, Users, Calendar, Clock, CreditCard, Globe, Phone, Mail, MapPin,
  SkipForward, Settings
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type Currency } from "@/lib/currency";

// Profession categories
const PROFESSION_CATEGORIES = [
  { value: "event_planner", label: "Event Planner", description: "Weddings, parties, corporate events" },
  { value: "wedding_coordinator", label: "Wedding Coordinator", description: "Full-service wedding planning" },
  { value: "creative_freelancer", label: "Creative Freelancer", description: "Designer, photographer, videographer" },
  { value: "fitness_coach", label: "Fitness Coach / Trainer", description: "Personal training, group fitness" },
  { value: "teacher_tutor", label: "Teacher / Tutor", description: "Classes, courses, workshops" },
  { value: "travel_coordinator", label: "Travel Coordinator", description: "Group trips, tours, adventures" },
  { value: "corporate_organizer", label: "Corporate Organizer", description: "HR, team events, office gifting" },
  { value: "community_leader", label: "Community Leader", description: "Clubs, societies, religious groups" },
  { value: "sports_coach", label: "Sports Coach", description: "Teams, leagues, tournaments" },
  { value: "consultant", label: "Consultant / Advisor", description: "Business, financial, legal services" },
  { value: "artist_performer", label: "Artist / Performer", description: "Musicians, actors, entertainers" },
  { value: "other", label: "Other", description: "Something else entirely" },
];

// Typical use cases
const USE_CASES = [
  { value: "group_gifts", label: "Group Gifts", icon: Gift, description: "Collecting for celebrations" },
  { value: "event_payments", label: "Event Payments", icon: Calendar, description: "Tickets, registrations, deposits" },
  { value: "recurring_fees", label: "Recurring Fees", icon: Clock, description: "Monthly dues, subscriptions" },
  { value: "project_payments", label: "Project Payments", icon: Briefcase, description: "Client deposits, milestones" },
  { value: "trip_collections", label: "Trip Collections", icon: Globe, description: "Group travel expenses" },
];

// Currencies with proper typing
const CURRENCIES: { value: Currency; label: string; symbol: string }[] = [
  { value: "INR", label: "Indian Rupee (₹)", symbol: "₹" },
  { value: "USD", label: "US Dollar ($)", symbol: "$" },
  { value: "EUR", label: "Euro (€)", symbol: "€" },
  { value: "GBP", label: "British Pound (£)", symbol: "£" },
  { value: "AUD", label: "Australian Dollar (A$)", symbol: "A$" },
  { value: "CAD", label: "Canadian Dollar (C$)", symbol: "C$" },
];

type OnboardingStep = "welcome" | "profile" | "business" | "payments" | "preferences" | "complete";

const STEPS: { key: OnboardingStep; title: string; icon: React.ReactNode }[] = [
  { key: "welcome", title: "Welcome", icon: <Briefcase className="h-4 w-4" /> },
  { key: "profile", title: "Your Profile", icon: <User className="h-4 w-4" /> },
  { key: "business", title: "Business Info", icon: <Building2 className="h-4 w-4" /> },
  { key: "payments", title: "Payments", icon: <Wallet className="h-4 w-4" /> },
  { key: "preferences", title: "Preferences", icon: <Bell className="h-4 w-4" /> },
  { key: "complete", title: "Complete", icon: <Rocket className="h-4 w-4" /> },
];

interface ProfileData {
  display_name: string;
  profession_category: string;
  profession_other: string;
  bio: string;
  website: string;
  phone: string;
  terminology_store: string;
  terminology_customers: string;
}

// Terminology options based on profession
const TERMINOLOGY_OPTIONS = {
  store: [
    { value: "practice", label: "Practice", description: "For healthcare, legal, consulting" },
    { value: "studio", label: "Studio", description: "For creative, fitness, arts" },
    { value: "business", label: "Business", description: "General professional services" },
    { value: "academy", label: "Academy", description: "For teaching, coaching, training" },
  ],
  customers: [
    { value: "clients", label: "Clients", description: "Professional relationship" },
    { value: "participants", label: "Participants", description: "For events, workshops, groups" },
    { value: "members", label: "Members", description: "For clubs, communities, subscriptions" },
    { value: "students", label: "Students", description: "For education, coaching" },
  ],
};

interface BusinessData {
  business_name: string;
  business_email: string;
  business_phone: string;
  business_address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  gst_number: string;
  pan_number: string;
}

interface PaymentData {
  currency: Currency;
  primary_use_cases: string[];
  typical_group_size: string;
  typical_amount_range: string;
  payment_method: "bank" | "skip";
  bank_account_name: string;
  bank_account_number: string;
  bank_account_confirm: string;
  bank_ifsc: string;
  bank_name: string;
}

interface PreferenceData {
  send_payment_reminders: boolean;
  reminder_frequency: string;
  auto_thank_you: boolean;
  allow_partial_payments: boolean;
  default_deadline_days: number;
  notify_on_payment: boolean;
  notify_on_completion: boolean;
}

const ProfessionalOnboarding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  const initialStep = (searchParams.get("step") as OnboardingStep) || "welcome";
  const [step, setStep] = useState<OnboardingStep>(initialStep);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accountCreationFailed, setAccountCreationFailed] = useState(false);

  // Profile data
  const [profile, setProfile] = useState<ProfileData>({
    display_name: "",
    profession_category: "",
    profession_other: "",
    bio: "",
    website: "",
    phone: "",
    terminology_store: "business",
    terminology_customers: "clients",
  });

  // Business data
  const [business, setBusiness] = useState<BusinessData>({
    business_name: "",
    business_email: "",
    business_phone: "",
    business_address: "",
    city: "",
    state: "",
    country: "India",
    postal_code: "",
    gst_number: "",
    pan_number: "",
  });

  // Payment data
  const [payments, setPayments] = useState<PaymentData>({
    currency: "INR",
    primary_use_cases: [],
    typical_group_size: "5-10",
    typical_amount_range: "1000-5000",
    payment_method: "skip",
    bank_account_name: "",
    bank_account_number: "",
    bank_account_confirm: "",
    bank_ifsc: "",
    bank_name: "",
  });

  // Preferences data
  const [preferences, setPreferences] = useState<PreferenceData>({
    send_payment_reminders: true,
    reminder_frequency: "smart",
    auto_thank_you: true,
    allow_partial_payments: false,
    default_deadline_days: 7,
    notify_on_payment: true,
    notify_on_completion: true,
  });

  // Fetch or create merchant data
  useEffect(() => {
    const fetchOrCreateMerchant = async () => {
      if (!user) return;
      
      try {
        const { data: merchantUser, error: muError } = await supabase
          .from("merchant_users")
          .select("merchant_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (muError) throw muError;
        
        if (!merchantUser) {
          // User exists but no merchant record - use atomic database function
          // This bypasses RLS timing issues by running with SECURITY DEFINER
          console.log("No merchant found, creating via setup_professional_merchant...");
          
          const { data: newMerchantId, error: setupError } = await supabase
            .rpc('setup_professional_merchant', {
              p_user_id: user.id,
              p_email: user.email || 'unknown@professional.shakatah.com'
            });

          if (setupError || !newMerchantId) {
            console.error("Error creating merchant via RPC:", setupError);
            toast.error("Failed to create account. Please sign out and try again.");
            setLoading(false);
            setAccountCreationFailed(true);
            return;
          }

          console.log("Merchant created successfully:", newMerchantId);
          setMerchantId(newMerchantId);
          toast.success("Account created! Let's set up your profile.");
        } else {
          setMerchantId(merchantUser.merchant_id);

          // Fetch merchant details
          const { data: merchant, error: mError } = await supabase
            .from("merchants")
            .select("*")
            .eq("id", merchantUser.merchant_id)
            .single();

          if (mError) throw mError;

          // Check if already onboarded
          if (merchant.onboarding_completed_at) {
            navigate("/merchant-dashboard");
            return;
          }

          // Pre-fill data
          setProfile(prev => ({
            ...prev,
            display_name: merchant.contact_person_name || "",
            phone: merchant.contact_person_phone || "",
          }));

          setBusiness(prev => ({
            ...prev,
            business_name: merchant.store_name || "",
            business_email: merchant.business_email || user.email || "",
            business_phone: merchant.business_phone || "",
            business_address: merchant.business_address || "",
            gst_number: merchant.gst_number || "",
            pan_number: merchant.pan_number || "",
          }));

          setPayments(prev => ({
            ...prev,
            currency: (merchant.currency as Currency) || "INR",
          }));
        }
      } catch (error) {
        console.error("Error fetching merchant:", error);
        toast.error("Failed to load account data");
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      if (!user) {
        navigate("/login?type=professional");
      } else {
        fetchOrCreateMerchant();
      }
    }
  }, [user, authLoading, navigate]);

  const handleStepChange = (newStep: OnboardingStep) => {
    setStep(newStep);
    setSearchParams({ step: newStep });
  };

  const getStepIndex = () => STEPS.findIndex(s => s.key === step);
  const progressPercent = ((getStepIndex() + 1) / STEPS.length) * 100;

  const saveProfileData = async () => {
    if (!merchantId) return;
    setSaving(true);

    try {
      // Build terminology preferences
      const terminology = {
        store: profile.terminology_store.charAt(0).toUpperCase() + profile.terminology_store.slice(1),
        customers: profile.terminology_customers.charAt(0).toUpperCase() + profile.terminology_customers.slice(1),
        orders: "Payments",
        products: "Services",
      };

      const { error } = await supabase
        .from("merchants")
        .update({
          contact_person_name: profile.display_name,
          contact_person_phone: profile.phone,
          store_category: profile.profession_category === "other" 
            ? profile.profession_other 
            : profile.profession_category,
          professional_terminology: terminology,
        })
        .eq("id", merchantId);

      if (error) throw error;
      toast.success("Profile saved!");
      handleStepChange("business");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveBusinessData = async () => {
    if (!merchantId) return;
    setSaving(true);

    try {
      const fullAddress = [
        business.business_address,
        business.city,
        business.state,
        business.postal_code,
        business.country
      ].filter(Boolean).join(", ");

      const { error } = await supabase
        .from("merchants")
        .update({
          store_name: business.business_name,
          business_email: business.business_email,
          business_phone: business.business_phone,
          business_address: fullAddress,
          gst_number: business.gst_number || null,
          pan_number: business.pan_number || null,
        })
        .eq("id", merchantId);

      if (error) throw error;
      toast.success("Business info saved!");
      handleStepChange("payments");
    } catch (error) {
      console.error("Error saving business:", error);
      toast.error("Failed to save business info");
    } finally {
      setSaving(false);
    }
  };

  const savePaymentData = async () => {
    if (!merchantId) return;
    setSaving(true);

    try {
      // Save currency to merchant
      const { error: merchantError } = await supabase
        .from("merchants")
        .update({
          currency: payments.currency,
        })
        .eq("id", merchantId);

      if (merchantError) throw merchantError;

      // If user provided bank details (not skipped), save to razorpay_linked_accounts
      if (payments.payment_method === "bank") {
        const accountData = {
          organizer_email: user?.email?.toLowerCase() || "",
          recipient_name: payments.bank_account_name,
          recipient_type: "professional",
          account_holder_name: payments.bank_account_name,
          bank_account_number: payments.bank_account_number,
          ifsc_code: payments.bank_ifsc.toUpperCase(),
          bank_name: payments.bank_name,
          account_status: "pending_verification",
        };

        // Check if account already exists
        const { data: existing } = await supabase
          .from("razorpay_linked_accounts")
          .select("id")
          .eq("organizer_email", user?.email?.toLowerCase() || "")
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("razorpay_linked_accounts")
            .update(accountData)
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("razorpay_linked_accounts")
            .insert(accountData);
          if (error) throw error;
        }
      }

      toast.success(payments.payment_method === "skip" 
        ? "Continuing without payment details - you can add them later"
        : "Payment settings saved!");
      handleStepChange("preferences");
    } catch (error) {
      console.error("Error saving payments:", error);
      toast.error("Failed to save payment settings");
    } finally {
      setSaving(false);
    }
  };

  const savePreferencesAndComplete = async () => {
    if (!merchantId) return;
    setSaving(true);

    try {
      // Update merchant
      const { error: merchantError } = await supabase
        .from("merchants")
        .update({
          default_deadline_hours: preferences.default_deadline_days * 24,
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq("id", merchantId);

      if (merchantError) throw merchantError;

      // Create notification preferences
      const { error: notifError } = await supabase
        .from("merchant_notification_preferences")
        .upsert({
          merchant_id: merchantId,
          reminder_enabled: preferences.send_payment_reminders,
          notify_payment_received: preferences.notify_on_payment,
          notify_order_completed: preferences.notify_on_completion,
        });

      if (notifError) console.error("Notification prefs error:", notifError);

      // Celebration!
      const colors = ["#0891B2", "#22D3EE", "#67E8F9"]; // Cyan palette
      const end = Date.now() + 3000;
      const frame = () => {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();

      toast.success("🎉 You're all set!");
      handleStepChange("complete");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast.error("Failed to complete setup");
    } finally {
      setSaving(false);
    }
  };

  const toggleUseCase = (value: string) => {
    setPayments(prev => ({
      ...prev,
      primary_use_cases: prev.primary_use_cases.includes(value)
        ? prev.primary_use_cases.filter(v => v !== value)
        : [...prev.primary_use_cases, value],
    }));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-background to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Show error UI if account creation failed
  if (accountCreationFailed) {
    const handleSignOut = async () => {
      await supabase.auth.signOut();
      navigate("/login?type=professional&tab=signup");
    };

    const handleRetry = () => {
      setAccountCreationFailed(false);
      setLoading(true);
      window.location.reload();
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-background to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Account Setup Failed</CardTitle>
            <CardDescription>
              We couldn't complete your account setup. This may be a temporary issue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleRetry} className="w-full" variant="default">
              Try Again
            </Button>
            <Button onClick={handleSignOut} className="w-full" variant="outline">
              Sign Out & Start Fresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-background to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <Gift className="w-5 h-5 text-cyan-600" />
            Shakatah
          </Link>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Briefcase className="w-4 h-4 text-cyan-600" />
            Professional Setup
          </div>
        </div>
      </header>

      <div className="container max-w-4xl px-4 sm:px-6 py-4 sm:py-8">
        {/* Progress */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm font-medium text-muted-foreground">
              Step {getStepIndex() + 1} of {STEPS.length}
            </span>
            <span className="text-xs sm:text-sm font-medium text-cyan-600">
              {Math.round(progressPercent)}% complete
            </span>
          </div>
          <Progress value={progressPercent} className="h-1.5 sm:h-2" />
          
          {/* Step indicators - simplified on mobile */}
          <div className="flex justify-between mt-3 sm:mt-4 gap-1">
            {STEPS.map((s, i) => (
              <div 
                key={s.key}
                className={`flex flex-col items-center gap-1 flex-1 ${
                  i <= getStepIndex() ? "text-cyan-600" : "text-muted-foreground"
                }`}
              >
                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm ${
                  i < getStepIndex() 
                    ? "bg-cyan-600 text-white" 
                    : i === getStepIndex()
                    ? "bg-cyan-100 dark:bg-cyan-950 text-cyan-600 ring-2 ring-cyan-600"
                    : "bg-muted"
                }`}>
                  {i < getStepIndex() ? <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" /> : <span className="[&>svg]:h-3 [&>svg]:w-3 sm:[&>svg]:h-4 sm:[&>svg]:w-4">{s.icon}</span>}
                </div>
                <span className="text-[10px] sm:text-xs text-center leading-tight hidden xs:block sm:block">{s.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="border-cyan-200 dark:border-cyan-900/30 mx-0">
          {/* Welcome Step */}
          {step === "welcome" && (
            <>
              <CardHeader className="text-center pb-4 px-4 sm:px-6">
                <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-cyan-100 dark:bg-cyan-950 flex items-center justify-center mb-3 sm:mb-4">
                  <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-600" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">Welcome, Professional!</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Let's set up your account to start collecting group payments from clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {[
                    { icon: Users, title: "Invite Participants", desc: "Share a link, they pay their share" },
                    { icon: Bell, title: "Auto Reminders", desc: "Smart notifications keep everyone on track" },
                    { icon: Shield, title: "Secure Payments", desc: "Razorpay & Stripe for peace of mind" },
                    { icon: CreditCard, title: "Easy Payouts", desc: "Money goes directly to your account" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 sm:p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30">
                      <div className="p-1.5 sm:p-2 rounded-md bg-cyan-100 dark:bg-cyan-900 shrink-0">
                        <item.icon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm">{item.title}</h4>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-3 p-3 sm:p-4 rounded-lg border border-cyan-200 dark:border-cyan-900/30">
                  <Checkbox 
                    id="terms" 
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="grid gap-1 leading-none min-w-0">
                    <label htmlFor="terms" className="text-sm font-medium cursor-pointer">
                      I agree to the Terms of Service and Privacy Policy
                    </label>
                    <p className="text-xs text-muted-foreground">
                      By continuing, you accept our{" "}
                      <Link to="/terms-of-service" className="text-cyan-600 hover:underline">terms</Link>
                      {" "}and{" "}
                      <Link to="/privacy-policy" className="text-cyan-600 hover:underline">privacy policy</Link>
                    </p>
                  </div>
                </div>

                <Button 
                  className="w-full bg-cyan-600 hover:bg-cyan-700" 
                  size="lg"
                  disabled={!termsAccepted}
                  onClick={() => handleStepChange("profile")}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {/* Profile Step */}
          {step === "profile" && (
            <>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                  Your Profile
                </CardTitle>
                <CardDescription className="text-sm">
                  Tell us about yourself so clients know who they're paying
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="display_name" className="text-sm">Your Name *</Label>
                    <Input
                      id="display_name"
                      placeholder="John Doe"
                      value={profile.display_name}
                      onChange={(e) => setProfile(p => ({ ...p, display_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                    <Input
                      id="phone"
                      placeholder="+91 98765 43210"
                      value={profile.phone}
                      onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession" className="text-sm">What best describes you? *</Label>
                  <Select 
                    value={profile.profession_category}
                    onValueChange={(v) => setProfile(p => ({ ...p, profession_category: v }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select your profession" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-background z-50">
                      {PROFESSION_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex flex-col py-0.5">
                            <span className="text-sm">{cat.label}</span>
                            <span className="text-xs text-muted-foreground">{cat.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {profile.profession_category === "other" && (
                  <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                    <Label htmlFor="profession_other" className="text-sm">Please specify *</Label>
                    <Input
                      id="profession_other"
                      placeholder="e.g., Life Coach, Art Teacher"
                      value={profile.profession_other}
                      onChange={(e) => setProfile(p => ({ ...p, profession_other: e.target.value }))}
                      autoFocus
                    />
                  </div>
                )}

                {/* Terminology Preferences */}
                <div className="space-y-3 p-3 sm:p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-900/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="h-4 w-4 text-cyan-600" />
                    <Label className="text-sm font-medium">Personalize Your Dashboard</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Choose the terminology that best fits your profession. You can change this later.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="terminology_store" className="text-xs">Your business is called a...</Label>
                      <Select 
                        value={profile.terminology_store}
                        onValueChange={(v) => setProfile(p => ({ ...p, terminology_store: v }))}
                      >
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          {TERMINOLOGY_OPTIONS.store.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className="text-sm">{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="terminology_customers" className="text-xs">Your customers are called...</Label>
                      <Select 
                        value={profile.terminology_customers}
                        onValueChange={(v) => setProfile(p => ({ ...p, terminology_customers: v }))}
                      >
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          {TERMINOLOGY_OPTIONS.customers.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className="text-sm">{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm">Short Bio (optional)</Label>
                  <Textarea
                    id="bio"
                    placeholder="A few words about what you do..."
                    value={profile.bio}
                    onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website" className="text-sm">Website (optional)</Label>
                  <Input
                    id="website"
                    placeholder="https://yourwebsite.com"
                    value={profile.website}
                    onChange={(e) => setProfile(p => ({ ...p, website: e.target.value }))}
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => handleStepChange("welcome")}
                    className="w-full sm:w-auto"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={!profile.display_name || !profile.profession_category || (profile.profession_category === "other" && !profile.profession_other) || saving}
                    onClick={saveProfileData}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Business Step */}
          {step === "business" && (
            <>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                  Business Information
                </CardTitle>
                <CardDescription className="text-sm">
                  Your business details for invoices and legal compliance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-sm">Business Name (optional)</Label>
                  <Input
                    id="business_name"
                    placeholder="Your Business or Brand Name"
                    value={business.business_name}
                    onChange={(e) => setBusiness(b => ({ ...b, business_name: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_email" className="text-sm">Business Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="business_email"
                        type="email"
                        className="pl-10"
                        placeholder="you@business.com"
                        value={business.business_email}
                        onChange={(e) => setBusiness(b => ({ ...b, business_email: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_phone" className="text-sm">Business Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="business_phone"
                        className="pl-10"
                        placeholder="+91 98765 43210"
                        value={business.business_phone}
                        onChange={(e) => setBusiness(b => ({ ...b, business_phone: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_address" className="text-sm">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="business_address"
                      className="pl-10"
                      placeholder="Street address"
                      value={business.business_address}
                      onChange={(e) => setBusiness(b => ({ ...b, business_address: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm">City</Label>
                    <Input
                      id="city"
                      placeholder="Mumbai"
                      value={business.city}
                      onChange={(e) => setBusiness(b => ({ ...b, city: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm">State</Label>
                    <Input
                      id="state"
                      placeholder="Maharashtra"
                      value={business.state}
                      onChange={(e) => setBusiness(b => ({ ...b, state: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code" className="text-sm">PIN Code</Label>
                    <Input
                      id="postal_code"
                      placeholder="400001"
                      value={business.postal_code}
                      onChange={(e) => setBusiness(b => ({ ...b, postal_code: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-sm">Country</Label>
                    <Input
                      id="country"
                      placeholder="India"
                      value={business.country}
                      onChange={(e) => setBusiness(b => ({ ...b, country: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gst_number" className="text-sm">GST Number (optional)</Label>
                    <Input
                      id="gst_number"
                      placeholder="22AAAAA0000A1Z5"
                      value={business.gst_number}
                      onChange={(e) => setBusiness(b => ({ ...b, gst_number: e.target.value.toUpperCase() }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_number" className="text-sm">PAN Number (optional)</Label>
                    <Input
                      id="pan_number"
                      placeholder="AAAAA1234A"
                      value={business.pan_number}
                      onChange={(e) => setBusiness(b => ({ ...b, pan_number: e.target.value.toUpperCase() }))}
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <Button variant="outline" onClick={() => handleStepChange("profile")} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={!business.business_email || saving}
                    onClick={saveBusinessData}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Payments Step */}
          {step === "payments" && (
            <>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                  Payment Preferences
                </CardTitle>
                <CardDescription className="text-sm">
                  How you'll collect and receive payments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="space-y-2">
                  <Label className="text-sm">Currency</Label>
                  <Select 
                    value={payments.currency}
                    onValueChange={(v) => setPayments(p => ({ ...p, currency: v as Currency }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm">What will you primarily use Shakatah for?</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {USE_CASES.map((uc) => (
                      <div
                        key={uc.value}
                        onClick={() => toggleUseCase(uc.value)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          payments.primary_use_cases.includes(uc.value)
                            ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-950/30"
                            : "border-border hover:border-cyan-400"
                        }`}
                      >
                        <div className={`p-1.5 sm:p-2 rounded-md shrink-0 ${
                          payments.primary_use_cases.includes(uc.value)
                            ? "bg-cyan-100 dark:bg-cyan-900 text-cyan-600"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          <uc.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{uc.label}</p>
                          <p className="text-xs text-muted-foreground truncate">{uc.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payout Details Section */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label className="text-sm sm:text-base font-semibold">Payout Details</Label>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Where should we send collected payments?
                    </p>
                  </div>

                  <RadioGroup
                    value={payments.payment_method}
                    onValueChange={(v) => setPayments(p => ({ ...p, payment_method: v as "bank" | "skip" }))}
                    className="grid grid-cols-2 gap-2 sm:gap-3"
                  >
                    <Label
                      htmlFor="payment-bank"
                      className={`flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        payments.payment_method === "bank"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-950/30"
                          : "border-border hover:border-cyan-400"
                      }`}
                    >
                      <RadioGroupItem value="bank" id="payment-bank" className="sr-only" />
                      <Building2 className={`h-5 w-5 sm:h-6 sm:w-6 ${payments.payment_method === "bank" ? "text-cyan-600" : "text-muted-foreground"}`} />
                      <span className="font-medium text-xs sm:text-sm">Bank Transfer</span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground text-center hidden sm:block">NEFT/IMPS Payout</span>
                    </Label>
                    <Label
                      htmlFor="payment-skip"
                      className={`flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        payments.payment_method === "skip"
                          ? "border-muted-foreground bg-muted/50"
                          : "border-border hover:border-muted-foreground"
                      }`}
                    >
                      <RadioGroupItem value="skip" id="payment-skip" className="sr-only" />
                      <SkipForward className={`h-5 w-5 sm:h-6 sm:w-6 ${payments.payment_method === "skip" ? "text-muted-foreground" : "text-muted-foreground/50"}`} />
                      <span className="font-medium text-xs sm:text-sm">Add Later</span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground text-center hidden sm:block">Before first collection</span>
                    </Label>
                  </RadioGroup>

                  {/* Bank Fields */}
                  {payments.payment_method === "bank" && (
                    <div className="space-y-3 p-3 sm:p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-900/30 animate-in slide-in-from-top-2 duration-200">
                      <div className="space-y-2">
                        <Label htmlFor="bank_account_name" className="text-sm">Account Holder Name *</Label>
                        <Input
                          id="bank_account_name"
                          placeholder="As per bank records"
                          value={payments.bank_account_name}
                          onChange={(e) => setPayments(p => ({ ...p, bank_account_name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_account_number" className="text-sm">Account Number *</Label>
                        <Input
                          id="bank_account_number"
                          placeholder="Enter account number"
                          value={payments.bank_account_number}
                          onChange={(e) => setPayments(p => ({ ...p, bank_account_number: e.target.value.replace(/\D/g, "") }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_account_confirm" className="text-sm">Confirm Account Number *</Label>
                        <Input
                          id="bank_account_confirm"
                          placeholder="Re-enter account number"
                          value={payments.bank_account_confirm}
                          onChange={(e) => setPayments(p => ({ ...p, bank_account_confirm: e.target.value.replace(/\D/g, "") }))}
                        />
                        {payments.bank_account_confirm && payments.bank_account_number !== payments.bank_account_confirm && (
                          <p className="text-xs text-destructive">Account numbers don't match</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="bank_ifsc" className="text-sm">IFSC Code *</Label>
                          <Input
                            id="bank_ifsc"
                            placeholder="SBIN0001234"
                            value={payments.bank_ifsc}
                            onChange={(e) => setPayments(p => ({ ...p, bank_ifsc: e.target.value.toUpperCase() }))}
                            maxLength={11}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="bank_name" className="text-sm">Bank Name</Label>
                          <Input
                            id="bank_name"
                            placeholder="SBI, HDFC..."
                            value={payments.bank_name}
                            onChange={(e) => setPayments(p => ({ ...p, bank_name: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Skip Notice */}
                  {payments.payment_method === "skip" && (
                    <Alert className="border-muted bg-muted/30">
                      <SkipForward className="h-4 w-4 text-muted-foreground" />
                      <AlertDescription className="text-xs sm:text-sm">
                        You can add payment details later from your dashboard. You'll be prompted when you create your first payment collection.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Security Notice */}
                  {payments.payment_method !== "skip" && (
                    <Alert className="border-cyan-200 dark:border-cyan-900/30 bg-cyan-50 dark:bg-cyan-950/30">
                      <Shield className="h-4 w-4 text-cyan-600" />
                      <AlertDescription className="text-xs">
                        Your details are encrypted and stored securely. We use bank-grade security for all transactions.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <Button variant="outline" onClick={() => handleStepChange("business")} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button 
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={saving || (
                      payments.payment_method === "bank" && (
                        !payments.bank_account_name || 
                        !payments.bank_account_number || 
                        payments.bank_account_number !== payments.bank_account_confirm ||
                        payments.bank_ifsc.length !== 11
                      )
                    )}
                    onClick={savePaymentData}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {payments.payment_method === "skip" ? "Skip & Continue" : "Save & Continue"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Preferences Step */}
          {step === "preferences" && (
            <>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                  Notification & Reminder Settings
                </CardTitle>
                <CardDescription className="text-sm">
                  Customize how Shakatah works for you
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border">
                    <div className="space-y-0.5 min-w-0">
                      <Label className="font-medium text-sm">Send Payment Reminders</Label>
                      <p className="text-xs text-muted-foreground">
                        Automatically remind participants who haven't paid
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.send_payment_reminders}
                      onCheckedChange={(v) => setPreferences(p => ({ ...p, send_payment_reminders: v }))}
                      className="shrink-0"
                    />
                  </div>

                  {preferences.send_payment_reminders && (
                    <div className="ml-2 sm:ml-4 space-y-2 animate-in slide-in-from-top-2 duration-200">
                      <Label className="text-sm">Reminder Frequency</Label>
                      <RadioGroup 
                        value={preferences.reminder_frequency}
                        onValueChange={(v) => setPreferences(p => ({ ...p, reminder_frequency: v }))}
                        className="space-y-2"
                      >
                        <div className="flex items-start sm:items-center space-x-2">
                          <RadioGroupItem value="smart" id="smart" className="mt-0.5 sm:mt-0" />
                          <Label htmlFor="smart" className="font-normal text-sm leading-tight">Smart (recommended) - based on deadline proximity</Label>
                        </div>
                        <div className="flex items-start sm:items-center space-x-2">
                          <RadioGroupItem value="daily" id="daily" className="mt-0.5 sm:mt-0" />
                          <Label htmlFor="daily" className="font-normal text-sm">Daily reminders</Label>
                        </div>
                        <div className="flex items-start sm:items-center space-x-2">
                          <RadioGroupItem value="minimal" id="minimal" className="mt-0.5 sm:mt-0" />
                          <Label htmlFor="minimal" className="font-normal text-sm leading-tight">Minimal - only at 48h and 1h before deadline</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  <div className="flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border">
                    <div className="space-y-0.5 min-w-0">
                      <Label className="font-medium text-sm">Auto Thank You Messages</Label>
                      <p className="text-xs text-muted-foreground">
                        Send a thank you when someone pays
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.auto_thank_you}
                      onCheckedChange={(v) => setPreferences(p => ({ ...p, auto_thank_you: v }))}
                      className="shrink-0"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border">
                    <div className="space-y-0.5 min-w-0">
                      <Label className="font-medium text-sm">Notify Me on Payment</Label>
                      <p className="text-xs text-muted-foreground">
                        Get notified when someone pays
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.notify_on_payment}
                      onCheckedChange={(v) => setPreferences(p => ({ ...p, notify_on_payment: v }))}
                      className="shrink-0"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border">
                    <div className="space-y-0.5 min-w-0">
                      <Label className="font-medium text-sm">Notify Me on Completion</Label>
                      <p className="text-xs text-muted-foreground">
                        Get notified when all payments are collected
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.notify_on_completion}
                      onCheckedChange={(v) => setPreferences(p => ({ ...p, notify_on_completion: v }))}
                      className="shrink-0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Default Payment Deadline</Label>
                  <Select 
                    value={preferences.default_deadline_days.toString()}
                    onValueChange={(v) => setPreferences(p => ({ ...p, default_deadline_days: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    You can change this per collection
                  </p>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <Button variant="outline" onClick={() => handleStepChange("payments")} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button 
                    variant="ghost"
                    className="w-full sm:w-auto text-muted-foreground hover:text-foreground"
                    disabled={saving}
                    onClick={savePreferencesAndComplete}
                  >
                    <SkipForward className="mr-2 h-4 w-4" />
                    Skip for now
                  </Button>
                  <Button 
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={saving}
                    onClick={savePreferencesAndComplete}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Complete Setup
                    <Rocket className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Complete Step */}
          {step === "complete" && (
            <>
              <CardHeader className="text-center px-4 sm:px-6">
                <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center mb-3 sm:mb-4">
                  <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">You're All Set! 🎉</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Your professional account is ready to accept group payments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <button 
                    onClick={() => navigate("/merchant-dashboard?tab=overview&action=create-collection")}
                    className="p-3 sm:p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 text-center hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors cursor-pointer border-2 border-transparent hover:border-cyan-300 dark:hover:border-cyan-700"
                  >
                    <Users className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-600 mx-auto mb-2" />
                    <h4 className="font-medium text-sm">Create Your First Collection</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Start collecting payments from your clients
                    </p>
                  </button>
                  <button 
                    onClick={() => navigate("/merchant-dashboard?tab=settings&sub=billing")}
                    className="p-3 sm:p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 text-center hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors cursor-pointer border-2 border-transparent hover:border-cyan-300 dark:hover:border-cyan-700"
                  >
                    <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-600 mx-auto mb-2" />
                    <h4 className="font-medium text-sm">Connect Your Bank</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add bank details to receive payouts
                    </p>
                  </button>
                </div>

                <Button 
                  className="w-full bg-cyan-600 hover:bg-cyan-700" 
                  size="lg"
                  onClick={() => navigate("/merchant-dashboard")}
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProfessionalOnboarding;
